/** Approved music and Game 3 samples, routed through the shared mute/volume rig. */
import { getRig, initRig, isMutedInternal, isRunning } from './context';

type Track = 'game1' | 'game2' | 'game3';
type Cue = 'accident' | 'card-place';
const files = {
  game1: 'academy-energy-128bpm-loop.wav',
  game2: 'game2-factory-bounce-132bpm-loop.wav',
  game3: 'game3-circular-groove-124bpm-loop.wav',
  accident: 'game3-accident-alert.wav',
  'card-place': 'game3-action-card-place.wav',
};
let track: Track | undefined;
let generation = 0;
let source: AudioBufferSourceNode | undefined;
let musicGain: GainNode | undefined;
let paused = false;
let offset = 0;
let startedAt = 0;
let loading = false;
const buffers = new Map<string, Promise<AudioBuffer>>();
const effects = new Set<AudioBufferSourceNode>();

function load(key: Track | Cue): Promise<AudioBuffer> {
  let promise = buffers.get(key);
  if (!promise) {
    const rig = getRig();
    if (!rig) return Promise.reject(new Error('Audio not ready'));
    promise = fetch(`${import.meta.env.BASE_URL}audio/${files[key]}`)
      .then(r => { if (!r.ok) throw new Error('Audio unavailable'); return r.arrayBuffer(); })
      .then(data => rig.ctx.decodeAudioData(data));
    buffers.set(key, promise);
    void promise.catch(() => buffers.delete(key));
  }
  return promise;
}

export async function applyPendingMusic(): Promise<void> {
  const rig = getRig();
  if (!track || !rig || !isRunning() || document.hidden || source || paused || loading) return;
  const token = generation;
  const selected = track;
  loading = true;
  try {
    if (selected === 'game3') {
      void load('accident').catch(() => {});
      void load('card-place').catch(() => {});
    }
    const buffer = await load(selected);
    if (token !== generation || paused || source || document.hidden) return;
    source = rig.ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    musicGain = rig.ctx.createGain();
    musicGain.gain.value = 0.3;
    source.connect(musicGain).connect(rig.warmth);
    startedAt = rig.ctx.currentTime;
    source.start(0, offset % buffer.duration);
  } catch { /* Optional media never blocks a game. */ }
  finally { if (token === generation) loading = false; }
}

function unlock(): void {
  if (track && !document.hidden) void initRig().then(applyPendingMusic).catch(() => {});
}
function visibility(): void {
  if (document.hidden) {
    for (const effect of effects) effect.stop();
    effects.clear();
  } else void applyPendingMusic();
}

export function startGameMusic(id: Track): void {
  stopGameMusic();
  track = id;
  window.addEventListener('pointerdown', unlock, true);
  window.addEventListener('keydown', unlock, true);
  document.addEventListener('visibilitychange', visibility);
  void applyPendingMusic();
}

export function setMusicPaused(value: boolean): void {
  if (paused === value) return;
  paused = value;
  if (value && source) {
    offset += (getRig()?.ctx.currentTime ?? startedAt) - startedAt;
    source.stop(); source.disconnect(); source = undefined;
    musicGain?.disconnect(); musicGain = undefined;
  } else if (!value) void applyPendingMusic();
}

export async function playGameSample(cue: Cue): Promise<void> {
  const rig = getRig();
  if (track !== 'game3' || !rig || !isRunning() || isMutedInternal() || document.hidden) return;
  const token = generation;
  const at = performance.now();
  try {
    const buffer = await load(cue);
    if (generation !== token || isMutedInternal() || !isRunning() || document.hidden || performance.now() - at > 500) return;
    const effect = rig.ctx.createBufferSource();
    const gain = rig.ctx.createGain();
    gain.gain.value = 0.85;
    effect.buffer = buffer;
    effect.connect(gain).connect(rig.sfx);
    effects.add(effect);
    effect.onended = () => { effects.delete(effect); effect.disconnect(); gain.disconnect(); };
    effect.start();
  } catch { /* Optional feedback. */ }
}

export function stopGameMusic(): void {
  generation++;
  track = undefined; paused = false; loading = false; offset = 0;
  source?.stop(); source?.disconnect(); source = undefined;
  musicGain?.disconnect(); musicGain = undefined;
  for (const effect of effects) effect.stop();
  effects.clear(); buffers.clear();
  window.removeEventListener('pointerdown', unlock, true);
  window.removeEventListener('keydown', unlock, true);
  document.removeEventListener('visibilitychange', visibility);
}
